import { DateTime } from "luxon";

export enum SortDirection {
  Ascending = "asc",
  Descending = "desc",
}

type SortDir = SortDirection | `${SortDirection}`;
type SortPrimitive = string | number | boolean;
export type SortFn<T = any> = (a: T, b: T, direction?: SortDir) => number;

export abstract class SortUtil {
  /**
   * Sort a collection by comparing using multiple functions.
   *
   * This compares by the first function in the sorts list.
   * If that comparison is equal, it then compares by the second function in the sorts list, and so on.
   *
   * @example
   * // Sort by name, then break ties by ID.
   * values.sort((a, b) => SortUtil.compareByMultiple(a, b, [
   *   SortUtil.compare(a.name, b.name),
   *   SortUtil.compare(a.id, b.id),
   * ])
   *
   * @param sorts The series of sort functions to apply to the collection.
   */
  public static compareByMultiple<T>(sorts: SortFn<T>[]): SortFn<T> {
    return (a: T, b: T) => {
      for (const sort of sorts) {
        const result = sort(a, b);
        if (result === 0) {
          continue;
        }
        return result;
      }
      return 0;
    };
  }

  /**
   * Compare two values that are a string, number, or boolean.
   *
   * @example
   * values.sort((a, b) => SortUtil.compare(a, b));
   */
  public static compare<T = SortPrimitive>(a: T, b: T, direction: SortDir = SortDirection.Ascending): number {
    if (a === b) {
      return 0;
    }
    return (a < b ? -1 : 1) * (direction === SortDirection.Ascending ? 1 : -1);
  }

  /**
   * Compare two strings in a case-insensitive fashion.
   * (Both strings get converted to lowercase before comparison.)
   *
   * @example
   * names.sort((a, b) => SortUtil.compareCaseInsensitive(a, b));
   */
  public static compareCaseInsensitive(a: string, b: string, direction: SortDir = SortDirection.Ascending): number {
    return SortUtil.compare(a.toLowerCase(), b.toLowerCase(), direction);
  }

  /**
   * Compare two JavaScript dates.
   *
   * @example
   * dates.sort((a, b) => SortUtil.compare(a, b));
   */
  public static compareDate(a: Date, b: Date, direction?: SortDir): number;
  /**
   * Compare two Luxon DateTimes.
   *
   * @example
   * dates.sort((a, b) => SortUtil.compare(a, b));
   */
  public static compareDate(a: DateTime, b: DateTime, direction?: SortDir): number;
  public static compareDate(a: Date | DateTime, b: Date | DateTime, direction?: SortDir): number {
    return SortUtil.compare(SortUtil.getMilliseconds(a), SortUtil.getMilliseconds(b), direction);
  }

  private static getMilliseconds(date: Date | DateTime): number {
    if (date instanceof Date) {
      return date.getTime();
    } else {
      return date.toMillis();
    }
  }
}
